export const AUTH0_DOMAIN = process.env.NEXT_PUBLIC_AUTH0_DOMAIN ?? '';

export const AUTH0_CLIENT_ID = process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID ?? '';

export const AUTH0_ALLOWED_ORIGIN = process.env.NEXT_PUBLIC_AUTH0_ALLOWED_ORIGIN ?? '';

// Identifier of the Admin Portal API in Auth0. Access tokens requested for this audience carry a
// `permissions` claim filtered to the roles allowed in Admin Portal (RBAC on the API).
export const AUTH0_AUDIENCE = process.env.NEXT_PUBLIC_AUTH0_AUDIENCE ?? '';

export const AUTH0_MFA_ENABLED_CLAIM = 'https://springhealth.com/is_mfa_enabled';

// The user's Rotom user ID. Rotom reads it from the access token, so the app does too.
const AUTH0_LEGACY_ID_CLAIM = 'https://springhealth.com/legacy_id';

// Decodes an access token's payload. The token is not verified here; the backend does that.
const decodeAccessTokenPayload = (accessToken: string): Record<string, unknown> => {
  const payload = accessToken.split('.')[1] ?? '';

  return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
};

/**
 * Reads the `permissions` claim from an Auth0 access token. Each permission on the Admin Portal
 * API is named after the role that grants it (e.g. `group:admin`), so these are the user's
 * Admin Portal roles.
 */
export const getAccessTokenPermissions = (accessToken: string): string[] => {
  const { permissions } = decodeAccessTokenPayload(accessToken);

  return Array.isArray(permissions) ? permissions.filter((permission) => typeof permission === 'string') : [];
};

/**
 * Reads the Rotom user ID from an Auth0 access token, or undefined when the claim is missing.
 */
export const getAccessTokenLegacyId = (accessToken: string): string | undefined => {
  const legacyId = decodeAccessTokenPayload(accessToken)[AUTH0_LEGACY_ID_CLAIM];

  return typeof legacyId === 'string' && legacyId ? legacyId : undefined;
};

/**
 * Returns `path` when it is a path on this site (e.g. `/admin/users?id=1`), otherwise undefined, so
 * the page restored after sign-in can never be another site. Rejects absolute and protocol-relative
 * URLs (`//evil.com`), backslashes, which browsers read as slashes (`/\evil.com`), and control
 * characters, which browsers strip (`/\t/evil.com`).
 */
export const getSafeReturnTo = (path: unknown): string | undefined => {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) {
    return undefined;
  }

  // eslint-disable-next-line no-control-regex
  return /[\\\u0000-\u001F\u007F]/.test(path) ? undefined : path;
};

export const getAuth0RedirectUri = () => {
  if (typeof window === 'undefined') {
    return AUTH0_ALLOWED_ORIGIN;
  }

  return `${window.location.origin}/`;
};
